import { Badge, StatusPill } from '@bb/ui';
import type { CanvasSimTraceItem } from '../../../api/client.ts';
import { abortText, CHANNEL_LABELS, type DeliveryChannel } from '../../composer/shared/personalization.ts';
import { formatOffset } from './simulatorForm.ts';

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const json = (v: unknown) => JSON.stringify(v, null, 2);

const KIND_LABELS: Record<CanvasSimTraceItem['kind'], string> = { enter: 'Enter', advance: 'Advance', wait: 'Wait', timer: 'Timer', event: 'Event', dispatch: 'Send', exit: 'Exit' };

const EVENT_RESULTS: Record<string, string> = {
  matched: 'matched what the step waited for',
  not_accepted: 'not accepted (filters or the window of the step)',
  not_waiting: 'nothing was waiting for it',
  exit_event: 'an exit event: the user left the Canvas',
  no_change: 'no change (the attribute already had this value)',
};

const DISPATCH_TONES: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'neutral'> = { sent: 'success', deferred: 'info', suppressed: 'warning', capped: 'warning', failed: 'danger' };

/** Reachability of a channel in a dry run: real for a test user, assumed for a synthetic one (no devices). */
function reachText(reachable: unknown): string {
  if (reachable === true) return 'Reachable';
  if (reachable === 'assumed') return 'Assumed reachable (a synthetic user has no devices)';
  if (typeof reachable === 'string') return `Unreachable: ${reachable}`;
  return 'Reachability unknown';
}

function ChannelResult({ channel, result }: { channel: string; result: Record<string, unknown> }) {
  const aborted = isObject(result.aborted) ? (result.aborted as { reason: string; detail?: string }) : null;
  const unresolved = Array.isArray(result.unresolved) ? (result.unresolved as string[]) : [];
  const defaults = Array.isArray(result.usedDefaults) ? (result.usedDefaults as string[]) : [];
  const label = CHANNEL_LABELS[channel as DeliveryChannel] ?? channel;
  return (
    <li className="sim-channel" aria-label={`Channel ${label}`}>
      <strong>{label}</strong> <span className="hint">{reachText(result.reachable)}</span>
      {typeof result.locale === 'string' && <span className="hint"> · {result.locale}</span>}
      {aborted && (
        <p className="field-error">
          Not sent: {abortText(aborted)}
          {aborted.detail ? ` (${aborted.detail})` : ''}
        </p>
      )}
      {unresolved.length > 0 && <p className="hint">Unresolved (render empty): {unresolved.join(', ')}</p>}
      {defaults.length > 0 && <p className="hint">Defaults used: {defaults.join(', ')}</p>}
      {result.payload !== undefined && (
        <details>
          <summary>Payload</summary>
          <pre className="sdk-log" aria-label={`${label} payload`}>
            {json(result.payload)}
          </pre>
        </details>
      )}
    </li>
  );
}

function Details({ item, stepName }: { item: CanvasSimTraceItem; stepName: (id: string | null) => string }) {
  const p = item.payload ?? {};
  switch (item.kind) {
    case 'enter': {
      const context = isObject(p.context) && Object.keys(p.context).length ? p.context : null;
      return (
        <>
          Entered variant <code>{item.port ?? String(p.variantId ?? '')}</code>
          {item.stepId ? <> at «{stepName(item.stepId)}»</> : ' (no first step)'}.
          {context && <pre className="sdk-log">{json(context)}</pre>}
        </>
      );
    }
    case 'advance': {
      const rest = Object.keys(p).length ? p : null;
      return (
        <>
          Left «{stepName(item.stepId)}» through <code>{item.port}</code>.
          {rest && (
            <details>
              <summary>Outcome</summary>
              <pre className="sdk-log">{json(rest)}</pre>
            </details>
          )}
        </>
      );
    }
    case 'wait': {
      const timers = Array.isArray(p.timers) ? p.timers.map(String) : [];
      const events = Array.isArray(p.events) ? p.events : [];
      return (
        <>
          Waiting in «{stepName(item.stepId)}»{timers.length ? ` until ${timers.join(', ')}` : ''}
          {events.length ? `; listening for ${events.map((e) => (typeof e === 'string' ? e : JSON.stringify(e))).join(', ')}` : ''}.
        </>
      );
    }
    case 'timer':
      return (
        <>
          Timer of «{stepName(item.stepId)}» fired{p.kind ? ` (${String(p.kind)})` : ''}.
        </>
      );
    case 'event':
      return (
        <>
          Scripted {String(p.type ?? 'event')}
          {p.name ? <> «{String(p.name)}»</> : null}
          {p.attribute ? <> {String(p.attribute)}</> : null}: {EVENT_RESULTS[item.reason ?? ''] ?? item.reason}.
        </>
      );
    case 'dispatch': {
      const status = String(p.status ?? item.reason ?? 'unknown');
      const channels = isObject(p.channels) ? Object.entries(p.channels).filter((e): e is [string, Record<string, unknown>] => isObject(e[1])) : [];
      return (
        <>
          «{stepName(item.stepId)}»: <StatusPill tone={DISPATCH_TONES[status] ?? 'neutral'}>{status}</StatusPill>
          {typeof p.until === 'string' ? ` until ${p.until}` : ''}
          {item.reason && item.reason !== status ? ` (${item.reason})` : ''}
          {channels.length > 0 && (
            <ul className="plain-list sim-channels">
              {channels.map(([channel, result]) => (
                <ChannelResult key={channel} channel={channel} result={result} />
              ))}
            </ul>
          )}
        </>
      );
    }
    case 'exit':
      return <>{p.rejected ? `Did not enter: ${item.reason}.` : item.reason === 'completed' ? 'Completed the Canvas.' : `Left the Canvas: ${item.reason ?? String(p.status ?? '')}.`}</>;
  }
}

/** The run in order (BB-122): virtual time since the start, what happened, and for every send the exact payload per channel. */
export function SimulationTimeline({ trace, stepName }: { trace: CanvasSimTraceItem[]; stepName: (id: string | null) => string }) {
  return (
    <ol className="sim-timeline" aria-label="Simulation timeline">
      {trace.map((item, i) => (
        <li key={i} className={`sim-timeline__item sim-timeline__item--${item.kind}`}>
          <span className="sim-timeline__time" title={item.at}>
            {formatOffset(item.offsetSeconds)}
          </span>{' '}
          <Badge tone={item.kind === 'dispatch' ? 'info' : item.kind === 'exit' ? 'success' : 'neutral'}>{KIND_LABELS[item.kind]}</Badge> <Details item={item} stepName={stepName} />
        </li>
      ))}
    </ol>
  );
}
